import { Component } from '@theme/component';

const DISMISSED_KEY = 'email-popup-dismissed-at';
const SUBSCRIBED_KEY = 'email-popup-subscribed';
const WHEEL_SPUN_KEY = 'email-popup-wheel-spun';
const CODE_KEY = 'email-popup-code';
const RESET_PARAM = 'email_popup_reset';
const WHEEL_SPIN_MS = 4500;
const WHEEL_REST_HOLD_MS = 1400;
const ENVELOPE_REVEAL_MS = 1750;
const ENVELOPE_HOLD_MS = 1500;
const CLOSE_FALLBACK_MS = 400;
const FINE_POINTER_QUERY = '(pointer: fine)';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

function trackEvent(name, params = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

function labelColorFor(color) {
  const hex = color.replace('#', '').trim();
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return '#181818';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.55 ? '#181818' : '#ffffff';
}

class EmailPopup extends Component {
  #wheel = null;
  #hasSpun = false;
  #lastFocused = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);

    const copyButton = this.querySelector('[data-email-popup-copy]');
    copyButton?.addEventListener('click', this.#copyCode);

    document.addEventListener('keydown', this.#handleKeydown);

    const didReset = this.#resetTestStateIfRequested();

    if (this.#justSubscribed()) {
      localStorage.setItem(SUBSCRIBED_KEY, 'true');
      trackEvent('email_popup_subscribe');
      this.#clearSubscribedParam();
      this.#showSuccessPanel();
      this.#open();
      return;
    }

    if (this.querySelector('.email-popup__error')) {
      this.#showFormPanel();
      this.#open();
      return;
    }

    if (localStorage.getItem(SUBSCRIBED_KEY) === 'true') {
      if (this.#showSuccessPanel()) this.#showBadge();
      return;
    }

    if (localStorage.getItem(WHEEL_SPUN_KEY) === 'true') {
      this.#showFormPanel();
    } else {
      this.#showOpenerPanel();
    }

    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    if (dismissedAt) {
      this.#showBadge();

      const frequencyDays = Number(this.dataset.frequencyDays || 14);
      const daysSinceDismiss = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < frequencyDays) return;
    }

    if (didReset) {
      this.#open();
      return;
    }

    this.#scheduleOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.clearTimeout(this.timeoutId);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  #scheduleOpen() {
    const delaySeconds = Number(this.dataset.delaySeconds || 5);
    this.timeoutId = window.setTimeout(() => this.#open(), delaySeconds * 1000);
  }

  #justSubscribed() {
    return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
  }

  #clearSubscribedParam() {
    const url = new URL(window.location.href);
    url.searchParams.delete('customer_posted');
    window.history.replaceState(window.history.state, '', url);
  }

  #resetTestStateIfRequested() {
    if (new URLSearchParams(window.location.search).get(RESET_PARAM) !== 'true') return false;

    localStorage.removeItem(SUBSCRIBED_KEY);
    localStorage.removeItem(WHEEL_SPUN_KEY);
    localStorage.removeItem(DISMISSED_KEY);
    localStorage.removeItem(CODE_KEY);

    const url = new URL(window.location.href);
    url.searchParams.delete(RESET_PARAM);
    window.history.replaceState(window.history.state, '', url);

    return true;
  }

  #showPanel(name) {
    this.querySelectorAll('[data-email-popup-panel]').forEach((panel) => {
      panel.toggleAttribute('hidden', panel.getAttribute('data-email-popup-panel') !== name);
    });
    this.querySelector('.email-popup__dialog')?.setAttribute('aria-labelledby', `email-popup-heading-${name}`);
  }

  #showOpenerPanel() {
    this.#showPanel('opener');
    this.#ensureWheel();
  }

  #showFormPanel() {
    this.#showPanel('form');
  }

  #showSuccessPanel() {
    const codeBox = this.querySelector('.email-popup__code');
    const codeElement = codeBox?.querySelector('span');
    const renderedCode = codeElement?.textContent?.trim();

    if (renderedCode) {
      localStorage.setItem(CODE_KEY, renderedCode);
    } else {
      const storedCode = localStorage.getItem(CODE_KEY);
      if (!storedCode || !codeElement) return false;
      codeElement.textContent = storedCode;
    }

    this.#showPanel('success');
    return true;
  }

  #ensureWheel() {
    if (this.#wheel) return this.#wheel;

    const container = this.querySelector('[data-email-popup-wheel-canvas]');
    if (!container || !window.spinWheel) return null;

    let labels = [];
    try {
      labels = JSON.parse(container.dataset.wheelLabels || '[]');
    } catch {
      return null;
    }
    if (labels.length === 0) return null;

    const styles = getComputedStyle(this);
    const foregroundRgb = styles.getPropertyValue('--color-foreground-rgb').trim();
    const font = styles.getPropertyValue('--email-popup-font-family').trim();
    const colors = (container.dataset.wheelColors || '')
      .split(',')
      .map((color) => color.trim())
      .filter(Boolean);
    const backgrounds = colors.length ? colors : ['#FFFFFF', '#E10202', '#FFFFFF', '#E10202', '#FFFFFF'];

    this.#wheel = new window.spinWheel.Wheel(container, {
      items: labels.map((label) => ({ label })),
      rotation: 360 / labels.length / 2,
      radius: 0.98,
      itemBackgroundColors: backgrounds,
      itemLabelColors: backgrounds.map(labelColorFor),
      itemLabelFont: font || 'sans-serif',
      itemLabelFontSizeMax: 40,
      itemLabelRadius: 0.9,
      itemLabelRadiusMax: 0.2,
      lineWidth: 2,
      lineColor: foregroundRgb ? `rgb(${foregroundRgb} / 0.35)` : '#00000059',
      isInteractive: false,
    });

    return this.#wheel;
  }

  #spinWheel = (button) => {
    if (this.#hasSpun) return;
    const wheel = this.#ensureWheel();
    if (!wheel) return;
    this.#hasSpun = true;
    button.setAttribute('disabled', '');

    const container = this.querySelector('[data-email-popup-wheel-canvas]');
    const winningIndex = Math.max(0, Number(container?.dataset.winningIndex || 1) - 1);

    wheel.onRest = () => {
      localStorage.setItem(WHEEL_SPUN_KEY, 'true');
      window.miniConfetti?.burst();
      window.setTimeout(() => {
        this.#showFormPanel();
        this.#focusForm();
      }, WHEEL_REST_HOLD_MS);
    };
    wheel.spinToItem(winningIndex, WHEEL_SPIN_MS, false, 6, 1);

    trackEvent('email_popup_wheel_spin');
  };

  #openEnvelope = () => {
    if (this.#hasSpun) return;
    const envelope = this.querySelector('.mhe');
    if (!envelope) return;
    this.#hasSpun = true;

    const seal = envelope.querySelector('.mhe__seal');
    if (seal && seal === document.activeElement) {
      this.querySelector('.email-popup__dialog')?.focus({ preventScroll: true });
    }
    seal?.setAttribute('disabled', '');
    envelope.classList.add('is-open');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealMs = reducedMotion ? 0 : ENVELOPE_REVEAL_MS;
    const holdMs = reducedMotion ? WHEEL_REST_HOLD_MS : ENVELOPE_HOLD_MS;

    window.setTimeout(() => {
      localStorage.setItem(WHEEL_SPUN_KEY, 'true');
      if (!reducedMotion) window.miniConfetti?.burst();
      window.setTimeout(() => {
        this.#showFormPanel();
        this.#focusForm();
      }, holdMs);
    }, revealMs);

    trackEvent('email_popup_envelope_open');
  };

  #focusForm() {
    if (window.matchMedia(FINE_POINTER_QUERY).matches) {
      this.querySelector('[data-email-popup-panel="form"] input[type="email"]')?.focus();
    } else {
      this.querySelector('.email-popup__dialog')?.focus({ preventScroll: true });
    }
  }

  #showBadge() {
    this.classList.remove('email-popup--visible', 'email-popup--closing');
    this.classList.add('email-popup--minimized');
  }

  #open(fromBadge = false) {
    window.clearTimeout(this.timeoutId);
    this.#lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.classList.remove('email-popup--minimized');
    this.classList.add('email-popup--visible');
    trackEvent('email_popup_view', { method: fromBadge ? 'badge' : 'auto' });

    const dialog = this.querySelector('.email-popup__dialog');
    window.requestAnimationFrame(() => dialog?.focus({ preventScroll: true }));
  }

  #close() {
    if (this.classList.contains('email-popup--closing')) return;
    this.classList.add('email-popup--closing');
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));

    const dialog = this.querySelector('.email-popup__dialog');
    const badge = this.querySelector('[data-email-popup-badge]');
    const returnTarget = this.#lastFocused && this.#lastFocused !== document.body ? this.#lastFocused : badge;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      dialog?.removeEventListener('transitionend', handleTransitionEnd);
      this.#showBadge();
      returnTarget?.focus({ preventScroll: true });
    };
    const handleTransitionEnd = (event) => {
      if (event.target === dialog && event.propertyName === 'opacity') finish();
    };

    dialog?.addEventListener('transitionend', handleTransitionEnd);
    window.setTimeout(finish, CLOSE_FALLBACK_MS);

    trackEvent('email_popup_dismiss');
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const spinButton = target.closest('[data-email-popup-spin]');

    if (target.closest('[data-email-popup-dismiss]')) {
      this.#close();
    } else if (target.closest('[data-email-popup-badge]')) {
      this.#open(true);
    } else if (target.closest('[data-email-popup-envelope]')) {
      this.#openEnvelope();
    } else if (spinButton instanceof HTMLButtonElement) {
      this.#spinWheel(spinButton);
    }
  };

  #handleKeydown = (event) => {
    if (!this.classList.contains('email-popup--visible')) return;

    if (event.key === 'Escape') {
      this.#close();
      return;
    }

    if (event.key !== 'Tab') return;

    const dialog = this.querySelector('.email-popup__dialog');
    if (!dialog) return;

    const focusable = [...dialog.querySelectorAll(FOCUSABLE)].filter(
      (element) => element instanceof HTMLElement && !element.closest('[hidden]')
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!dialog.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #copyCode = async () => {
    const codeElement = this.querySelector('.email-popup__code span');
    const code = codeElement?.textContent?.trim();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      trackEvent('email_popup_copy_code');
      const button = this.querySelector('[data-email-popup-copy]');
      if (button) {
        const original = button.innerHTML;
        button.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
        button.setAttribute('aria-label', 'Kopyalandı');
        window.setTimeout(() => {
          button.innerHTML = original;
          button.setAttribute('aria-label', 'Kopyala');
        }, 2000);
      }
    } catch {}
  };
}

if (!customElements.get('email-popup')) {
  customElements.define('email-popup', EmailPopup);
}
